import { useEffect, useMemo, useRef, useState } from 'react';
import './AccessibilityWidget.css';

type Lang='he'|'en'|'de'|'es'|'fr'|'it'|'ru'|'ar'|'pt'|'tr';
type AlignMode='default'|'right'|'center'|'justify';

type A11yState={
  fontScale:number;
  lineHeight:number;
  fontWeight:number;
  letterSpacing:number;
  readableFont:boolean;
  highContrast:boolean;
  lightContrast:boolean;
  monochrome:boolean;
  underlineLinks:boolean;
  largeCursor:boolean;
  hideImages:boolean;
  readingMask:boolean;
  readingLine:boolean;
  pauseMotion:boolean;
  highlightContent:boolean;
  alignText:AlignMode;
};

const DEFAULT_STATE:A11yState={
  fontScale:1,
  lineHeight:1,
  fontWeight:400,
  letterSpacing:0,
  readableFont:false,
  highContrast:false,
  lightContrast:false,
  monochrome:false,
  underlineLinks:false,
  largeCursor:false,
  hideImages:false,
  readingMask:false,
  readingLine:false,
  pauseMotion:false,
  highlightContent:false,
  alignText:'default',
};

const STORAGE_KEY='mielley_accessibility_v3';
const LANG_KEY='mielley_accessibility_lang_v3';

const LANGUAGES:{code:Lang;label:string;flag:string;dir:'rtl'|'ltr'}[]=[
  {code:'he',label:'עברית',flag:'🇮🇱',dir:'rtl'},
  {code:'en',label:'English',flag:'🇺🇸',dir:'ltr'},
  {code:'de',label:'Deutsch',flag:'🇩🇪',dir:'ltr'},
  {code:'es',label:'Español',flag:'🇪🇸',dir:'ltr'},
  {code:'fr',label:'Français',flag:'🇫🇷',dir:'ltr'},
  {code:'it',label:'Italiano',flag:'🇮🇹',dir:'ltr'},
  {code:'ru',label:'Русский',flag:'🇷🇺',dir:'ltr'},
  {code:'ar',label:'العربية',flag:'🇸🇦',dir:'rtl'},
  {code:'pt',label:'Português',flag:'🇵🇹',dir:'ltr'},
  {code:'tr',label:'Türkçe',flag:'🇹🇷',dir:'ltr'},
];

type Copy={
  title:string; subtitle:string; display:string; colors:string; orientation:string;
  skip:string; choose:string; main:string; nav:string; footer:string;
  readable:string; fontSize:string; lineHeight:string; bigCursor:string; fontWeight:string;
  align:string; letterSpacing:string; monochrome:string; highContrast:string; lightContrast:string;
  hideImages:string; readingMask:string; readingLine:string; links:string; stop:string;
  highlight:string; reset:string; done:string; default:string; close:string; open:string;
  alignmentRight:string; alignmentCenter:string; alignmentJustify:string; note:string;
};

const copy:Record<Lang,Copy>={
  he:{
    title:'התאמות נגישות',subtitle:'כלים לשיפור חוויית הגלישה באתר MIELLEY',
    display:'מודולי תצוגה',colors:'מודולי צבע',orientation:'מודולי התמצאות',
    skip:'דלגי לתוכן',choose:'...בחרי',main:'תוכן ראשי',nav:'ניווט',footer:'פוטר',
    readable:'גופן קריא',fontSize:'גודל גופן',lineHeight:'גובה שורה',bigCursor:'סמן גדול',
    fontWeight:'עובי גופן',align:'יישור טקסט',letterSpacing:'ריווח אותיות',
    monochrome:'שחור-לבן',highContrast:'ניגודיות גבוהה',lightContrast:'ניגודיות בהירה',
    hideImages:'הסתרת תמונות',readingMask:'מסכת קריאה',readingLine:'קו קריאה',
    links:'הדגשת קישורים',stop:'עצירת אנימציות',highlight:'הדגשת תוכן',
    reset:'איפוס התאמות',done:'סיום',default:'ברירת מחדל',close:'סגירה',open:'פתיחת תפריט נגישות',
    alignmentRight:'ימין',alignmentCenter:'מרכז',alignmentJustify:'מיושר',
    note:'כלי הנגישות מסייעים בהתאמת התצוגה. נגישות מלאה תלויה גם במבנה, בתוכן, בטפסים, במקלדת ובטקסטים חלופיים.'
  },
  en:{
    title:'Accessibility Adjustments',subtitle:'Tools to improve your browsing experience on MIELLEY',
    display:'Content Modules',colors:'Color Modules',orientation:'Orientation Modules',
    skip:'Skip To Content',choose:'...Choose',main:'Main Content',nav:'Navigation',footer:'Footer',
    readable:'Readable Font',fontSize:'Font Size',lineHeight:'Line Height',bigCursor:'Big Cursor',
    fontWeight:'Font Weight',align:'Align Text',letterSpacing:'Letter Spacing',
    monochrome:'Monochrome',highContrast:'High Contrast',lightContrast:'Light Contrast',
    hideImages:'Hide Images',readingMask:'Reading Mask',readingLine:'Reading Line',
    links:'Highlight Links',stop:'Stop Animations',highlight:'Highlight Content',
    reset:'Reset',done:'Done',default:'Default',close:'Close',open:'Open accessibility menu',
    alignmentRight:'Right',alignmentCenter:'Center',alignmentJustify:'Justify',
    note:'Accessibility tools assist with display preferences. Full accessibility also depends on structure, content, forms, keyboard operation and alternative text.'
  },
  de:{
    title:'Barrierefreiheit',subtitle:'Werkzeuge für ein besseres Nutzungserlebnis auf MIELLEY',
    display:'Inhaltsmodule',colors:'Farbmodule',orientation:'Orientierungsmodule',
    skip:'Zum Inhalt springen',choose:'...Auswählen',main:'Hauptinhalt',nav:'Navigation',footer:'Fußzeile',
    readable:'Lesbare Schrift',fontSize:'Schriftgröße',lineHeight:'Zeilenhöhe',bigCursor:'Großer Cursor',
    fontWeight:'Schriftstärke',align:'Textausrichtung',letterSpacing:'Zeichenabstand',
    monochrome:'Monochrom',highContrast:'Hoher Kontrast',lightContrast:'Heller Kontrast',
    hideImages:'Bilder ausblenden',readingMask:'Lesemaske',readingLine:'Leselinie',
    links:'Links hervorheben',stop:'Animationen stoppen',highlight:'Inhalt hervorheben',
    reset:'Zurücksetzen',done:'Fertig',default:'Standard',close:'Schließen',open:'Barrierefreiheitsmenü öffnen',
    alignmentRight:'Rechts',alignmentCenter:'Zentriert',alignmentJustify:'Blocksatz',
    note:'Diese Werkzeuge helfen bei der Darstellung. Vollständige Barrierefreiheit hängt auch von Struktur, Inhalten, Formularen, Tastaturbedienung und Alternativtexten ab.'
  },
  es:{
    title:'Ajustes de accesibilidad',subtitle:'Herramientas para mejorar tu experiencia en MIELLEY',
    display:'Módulos de contenido',colors:'Módulos de color',orientation:'Módulos de orientación',
    skip:'Saltar al contenido',choose:'...Elegir',main:'Contenido principal',nav:'Navegación',footer:'Pie de página',
    readable:'Fuente legible',fontSize:'Tamaño de fuente',lineHeight:'Altura de línea',bigCursor:'Cursor grande',
    fontWeight:'Grosor de fuente',align:'Alinear texto',letterSpacing:'Espaciado de letras',
    monochrome:'Monocromo',highContrast:'Alto contraste',lightContrast:'Contraste claro',
    hideImages:'Ocultar imágenes',readingMask:'Máscara de lectura',readingLine:'Línea de lectura',
    links:'Resaltar enlaces',stop:'Detener animaciones',highlight:'Resaltar contenido',
    reset:'Restablecer',done:'Listo',default:'Predeterminado',close:'Cerrar',open:'Abrir menú de accesibilidad',
    alignmentRight:'Derecha',alignmentCenter:'Centro',alignmentJustify:'Justificado',
    note:'Estas herramientas ayudan a adaptar la visualización. La accesibilidad completa también depende de la estructura, el contenido, los formularios, el teclado y los textos alternativos.'
  },
  fr:{
    title:'Réglages d’accessibilité',subtitle:'Des outils pour améliorer votre expérience sur MIELLEY',
    display:'Modules de contenu',colors:'Modules de couleur',orientation:'Modules d’orientation',
    skip:'Aller au contenu',choose:'...Choisir',main:'Contenu principal',nav:'Navigation',footer:'Pied de page',
    readable:'Police lisible',fontSize:'Taille du texte',lineHeight:'Hauteur de ligne',bigCursor:'Grand curseur',
    fontWeight:'Graisse du texte',align:'Alignement du texte',letterSpacing:'Espacement des lettres',
    monochrome:'Monochrome',highContrast:'Contraste élevé',lightContrast:'Contraste clair',
    hideImages:'Masquer les images',readingMask:'Masque de lecture',readingLine:'Ligne de lecture',
    links:'Surligner les liens',stop:'Arrêter les animations',highlight:'Surligner le contenu',
    reset:'Réinitialiser',done:'Terminer',default:'Par défaut',close:'Fermer',open:'Ouvrir le menu d’accessibilité',
    alignmentRight:'Droite',alignmentCenter:'Centre',alignmentJustify:'Justifié',
    note:'Ces outils facilitent l’adaptation de l’affichage. L’accessibilité complète dépend aussi de la structure, du contenu, des formulaires, du clavier et des textes alternatifs.'
  },
  it:{
    title:'Impostazioni di accessibilità',subtitle:'Strumenti per migliorare l’esperienza su MIELLEY',
    display:'Moduli contenuto',colors:'Moduli colore',orientation:'Moduli orientamento',
    skip:'Vai al contenuto',choose:'...Scegli',main:'Contenuto principale',nav:'Navigazione',footer:'Piè di pagina',
    readable:'Carattere leggibile',fontSize:'Dimensione carattere',lineHeight:'Altezza riga',bigCursor:'Cursore grande',
    fontWeight:'Peso carattere',align:'Allinea testo',letterSpacing:'Spaziatura lettere',
    monochrome:'Monocromatico',highContrast:'Contrasto elevato',lightContrast:'Contrasto chiaro',
    hideImages:'Nascondi immagini',readingMask:'Maschera di lettura',readingLine:'Linea di lettura',
    links:'Evidenzia link',stop:'Ferma animazioni',highlight:'Evidenzia contenuto',
    reset:'Ripristina',done:'Fine',default:'Predefinito',close:'Chiudi',open:'Apri menu accessibilità',
    alignmentRight:'Destra',alignmentCenter:'Centro',alignmentJustify:'Giustificato',
    note:'Questi strumenti aiutano ad adattare la visualizzazione. L’accessibilità completa dipende anche da struttura, contenuti, moduli, tastiera e testi alternativi.'
  },
  ru:{
    title:'Настройки доступности',subtitle:'Инструменты для улучшения работы с MIELLEY',
    display:'Модули контента',colors:'Цветовые модули',orientation:'Модули ориентации',
    skip:'Перейти к содержимому',choose:'...Выбрать',main:'Основное содержимое',nav:'Навигация',footer:'Подвал',
    readable:'Читаемый шрифт',fontSize:'Размер шрифта',lineHeight:'Высота строки',bigCursor:'Большой курсор',
    fontWeight:'Толщина шрифта',align:'Выравнивание текста',letterSpacing:'Интервал между буквами',
    monochrome:'Монохром',highContrast:'Высокий контраст',lightContrast:'Светлый контраст',
    hideImages:'Скрыть изображения',readingMask:'Маска чтения',readingLine:'Строка чтения',
    links:'Выделить ссылки',stop:'Остановить анимации',highlight:'Выделить содержимое',
    reset:'Сбросить',done:'Готово',default:'По умолчанию',close:'Закрыть',open:'Открыть меню доступности',
    alignmentRight:'Справа',alignmentCenter:'По центру',alignmentJustify:'По ширине',
    note:'Инструменты помогают настроить отображение. Полная доступность также зависит от структуры, контента, форм, клавиатуры и альтернативных текстов.'
  },
  ar:{
    title:'إعدادات إمكانية الوصول',subtitle:'أدوات لتحسين تجربة التصفح على MIELLEY',
    display:'وحدات المحتوى',colors:'وحدات الألوان',orientation:'وحدات التوجيه',
    skip:'الانتقال إلى المحتوى',choose:'...اختر',main:'المحتوى الرئيسي',nav:'التنقل',footer:'التذييل',
    readable:'خط سهل القراءة',fontSize:'حجم الخط',lineHeight:'ارتفاع السطر',bigCursor:'مؤشر كبير',
    fontWeight:'سُمك الخط',align:'محاذاة النص',letterSpacing:'تباعد الأحرف',
    monochrome:'أحادي اللون',highContrast:'تباين عالٍ',lightContrast:'تباين فاتح',
    hideImages:'إخفاء الصور',readingMask:'قناع القراءة',readingLine:'خط القراءة',
    links:'تمييز الروابط',stop:'إيقاف الرسوم المتحركة',highlight:'تمييز المحتوى',
    reset:'إعادة ضبط',done:'تم',default:'افتراضي',close:'إغلاق',open:'فتح قائمة إمكانية الوصول',
    alignmentRight:'يمين',alignmentCenter:'وسط',alignmentJustify:'ضبط',
    note:'تساعد هذه الأدوات على ضبط العرض. تعتمد إمكانية الوصول الكاملة أيضًا على البنية والمحتوى والنماذج ولوحة المفاتيح والنصوص البديلة.'
  },
  pt:{
    title:'Ajustes de acessibilidade',subtitle:'Ferramentas para melhorar a experiência na MIELLEY',
    display:'Módulos de conteúdo',colors:'Módulos de cor',orientation:'Módulos de orientação',
    skip:'Ir para o conteúdo',choose:'...Escolher',main:'Conteúdo principal',nav:'Navegação',footer:'Rodapé',
    readable:'Fonte legível',fontSize:'Tamanho da fonte',lineHeight:'Altura da linha',bigCursor:'Cursor grande',
    fontWeight:'Peso da fonte',align:'Alinhar texto',letterSpacing:'Espaçamento de letras',
    monochrome:'Monocromático',highContrast:'Alto contraste',lightContrast:'Contraste claro',
    hideImages:'Ocultar imagens',readingMask:'Máscara de leitura',readingLine:'Linha de leitura',
    links:'Destacar links',stop:'Parar animações',highlight:'Destacar conteúdo',
    reset:'Repor',done:'Concluir',default:'Padrão',close:'Fechar',open:'Abrir menu de acessibilidade',
    alignmentRight:'Direita',alignmentCenter:'Centro',alignmentJustify:'Justificado',
    note:'Estas ferramentas ajudam a adaptar a visualização. A acessibilidade completa também depende da estrutura, conteúdo, formulários, teclado e textos alternativos.'
  },
  tr:{
    title:'Erişilebilirlik ayarları',subtitle:'MIELLEY deneyimini iyileştirmek için araçlar',
    display:'İçerik modülleri',colors:'Renk modülleri',orientation:'Yönlendirme modülleri',
    skip:'İçeriğe geç',choose:'...Seç',main:'Ana içerik',nav:'Gezinme',footer:'Alt bilgi',
    readable:'Okunabilir yazı tipi',fontSize:'Yazı boyutu',lineHeight:'Satır yüksekliği',bigCursor:'Büyük imleç',
    fontWeight:'Yazı kalınlığı',align:'Metni hizala',letterSpacing:'Harf aralığı',
    monochrome:'Monokrom',highContrast:'Yüksek kontrast',lightContrast:'Açık kontrast',
    hideImages:'Görselleri gizle',readingMask:'Okuma maskesi',readingLine:'Okuma çizgisi',
    links:'Bağlantıları vurgula',stop:'Animasyonları durdur',highlight:'İçeriği vurgula',
    reset:'Sıfırla',done:'Tamam',default:'Varsayılan',close:'Kapat',open:'Erişilebilirlik menüsünü aç',
    alignmentRight:'Sağ',alignmentCenter:'Orta',alignmentJustify:'İki yana yasla',
    note:'Bu araçlar görüntüyü uyarlamaya yardımcı olur. Tam erişilebilirlik ayrıca yapı, içerik, formlar, klavye kullanımı ve alternatif metinlere bağlıdır.'
  }
};

function AccessibilityIcon(){
  return (
    <svg viewBox="0 0 64 64" aria-hidden="true">
      <circle cx="32" cy="10" r="6" fill="currentColor"/>
      <path d="M17 22c9 4 21 4 30 0M32 17v18M32 34 21 52M32 34l11 18"
        fill="none" stroke="currentColor" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function applyState(s:A11yState){
  const root=document.documentElement;
  root.style.setProperty('--mielley-a11y-font-scale',String(s.fontScale));
  root.style.setProperty('--mielley-a11y-line-height',String(s.lineHeight));
  root.style.setProperty('--mielley-a11y-font-weight',String(s.fontWeight));
  root.style.setProperty('--mielley-a11y-letter-spacing',`${s.letterSpacing}em`);

  const flags:Record<string,boolean>={
    'a11y-readable-font':s.readableFont,
    'a11y-high-contrast':s.highContrast,
    'a11y-light-contrast':s.lightContrast,
    'a11y-monochrome':s.monochrome,
    'a11y-underline-links':s.underlineLinks,
    'a11y-large-cursor':s.largeCursor,
    'a11y-hide-images':s.hideImages,
    'a11y-reading-mask':s.readingMask,
    'a11y-reading-line':s.readingLine,
    'a11y-pause-motion':s.pauseMotion,
    'a11y-highlight-content':s.highlightContent,
    'a11y-font-adjusted':s.fontScale!==1,
    'a11y-line-adjusted':s.lineHeight!==1,
    'a11y-weight-adjusted':s.fontWeight!==400,
    'a11y-spacing-adjusted':s.letterSpacing!==0,
    'a11y-align-right':s.alignText==='right',
    'a11y-align-center':s.alignText==='center',
    'a11y-align-justify':s.alignText==='justify',
  };
  Object.entries(flags).forEach(([cls,on])=>root.classList.toggle(cls,on));
}

export function AccessibilityWidget(){
  const [open,setOpen]=useState(false);
  const [lang,setLang]=useState<Lang>('he');
  const [langOpen,setLangOpen]=useState(false);
  const [state,setState]=useState<A11yState>(DEFAULT_STATE);
  const langBoxRef=useRef<HTMLDivElement|null>(null);

  const language=LANGUAGES.find(x=>x.code===lang)||LANGUAGES[0];
  const t=copy[lang];

  useEffect(()=>{
    try{
      const raw=localStorage.getItem(STORAGE_KEY);
      const savedLang=localStorage.getItem(LANG_KEY) as Lang|null;
      if(savedLang&&LANGUAGES.some(x=>x.code===savedLang))setLang(savedLang);
      if(raw){
        const parsed={...DEFAULT_STATE,...JSON.parse(raw)};
        setState(parsed);
        applyState(parsed);
      }else applyState(DEFAULT_STATE);
    }catch{applyState(DEFAULT_STATE)}
  },[]);

  useEffect(()=>{
    applyState(state);
    try{localStorage.setItem(STORAGE_KEY,JSON.stringify(state));}catch{}
  },[state]);

  useEffect(()=>{
    try{localStorage.setItem(LANG_KEY,lang);}catch{}
  },[lang]);

  useEffect(()=>{
    const onDoc=(e:MouseEvent)=>{
      if(langBoxRef.current&&!langBoxRef.current.contains(e.target as Node))setLangOpen(false);
    };
    document.addEventListener('mousedown',onDoc);
    return()=>document.removeEventListener('mousedown',onDoc);
  },[]);

  useEffect(()=>{
    const onMove=(e:MouseEvent)=>{
      document.documentElement.style.setProperty('--mielley-a11y-pointer-y',`${e.clientY}px`);
    };
    if(state.readingMask||state.readingLine){
      window.addEventListener('mousemove',onMove,{passive:true});
      return()=>window.removeEventListener('mousemove',onMove);
    }
  },[state.readingMask,state.readingLine]);

  useEffect(()=>{
    const videos=[...document.querySelectorAll('video')] as HTMLVideoElement[];
    if(state.pauseMotion)videos.forEach(v=>v.pause());
    else videos.filter(v=>v.autoplay).forEach(v=>{void v.play().catch(()=>{})});
  },[state.pauseMotion]);

  useEffect(()=>{
    if(!open)return;
    const onKey=(e:KeyboardEvent)=>{
      if(e.key==='Escape'){
        if(langOpen)setLangOpen(false);
        else setOpen(false);
      }
    };
    window.addEventListener('keydown',onKey);
    return()=>window.removeEventListener('keydown',onKey);
  },[open,langOpen]);

  const activeCount=useMemo(()=>[
    state.fontScale!==1,state.lineHeight!==1,state.fontWeight!==400,state.letterSpacing!==0,
    state.readableFont,state.highContrast,state.lightContrast,state.monochrome,state.underlineLinks,
    state.largeCursor,state.hideImages,state.readingMask,state.readingLine,state.pauseMotion,
    state.highlightContent,state.alignText!=='default'
  ].filter(Boolean).length,[state]);

  const toggle=(key:keyof Omit<A11yState,'fontScale'|'lineHeight'|'fontWeight'|'letterSpacing'|'alignText'>)=>{
    setState(s=>({...s,[key]:!s[key]}));
  };

  const reset=()=>setState(DEFAULT_STATE);

  const jumpTo=(value:string)=>{
    if(!value)return;
    const el=value==='main'
      ? (document.querySelector('main')||document.querySelector('#main-content'))
      : value==='nav' ? document.querySelector('nav')
      : document.querySelector('footer');
    if(el instanceof HTMLElement){
      const had=el.hasAttribute('tabindex');
      if(!had)el.setAttribute('tabindex','-1');
      el.scrollIntoView({behavior:'smooth',block:'start'});
      el.focus({preventScroll:true});
      if(!had)setTimeout(()=>el.removeAttribute('tabindex'),1200);
    }
  };

  const adjust=(key:'fontScale'|'lineHeight'|'fontWeight'|'letterSpacing',delta:number,min:number,max:number)=>{
    setState(s=>({...s,[key]:Math.min(max,Math.max(min,+((s[key] as number)+delta).toFixed(2)))}));
  };

  const cycleAlign=()=>{
    const order:AlignMode[]=['default','right','center','justify'];
    setState(s=>({...s,alignText:order[(order.indexOf(s.alignText)+1)%order.length]}));
  };

  const alignLabel=()=>{
    if(state.alignText==='default')return t.default;
    if(state.alignText==='right')return t.alignmentRight;
    if(state.alignText==='center')return t.alignmentCenter;
    return t.alignmentJustify;
  };

  return (
    <>
      <button type="button" className="mielley-a11y-fab" aria-label={t.open} aria-expanded={open} onClick={()=>setOpen(true)}>
        <AccessibilityIcon/>
        {activeCount>0&&<b>{activeCount}</b>}
      </button>

      {state.readingMask&&<div className="mielley-reading-mask" aria-hidden="true"/>}
      {state.readingLine&&<div className="mielley-reading-line" aria-hidden="true"/>}

      {open&&(
        <div className="mielley-a11y-backdrop" onMouseDown={e=>{if(e.currentTarget===e.target)setOpen(false)}}>
          <aside className="mielley-a11y-panel" role="dialog" aria-modal="true" aria-labelledby="mielley-a11y-title" dir={language.dir}>
            <div className="mielley-a11y-topbar">
              <div className="mielley-language-picker" ref={langBoxRef}>
                <button type="button" className="mielley-language-trigger" onClick={()=>setLangOpen(v=>!v)} aria-expanded={langOpen}>
                  <span className="mielley-language-chevron">{langOpen?'⌃':'⌄'}</span>
                  <span className="mielley-language-label">{language.label}</span>
                  <span className="mielley-language-flag">{language.flag}</span>
                </button>

                {langOpen&&(
                  <div className="mielley-language-menu" role="listbox" aria-label="Language">
                    {LANGUAGES.map(item=>(
                      <button
                        type="button"
                        role="option"
                        aria-selected={item.code===lang}
                        key={item.code}
                        className={`mielley-language-option ${item.code===lang?'is-selected':''}`}
                        dir={item.dir}
                        onClick={()=>{setLang(item.code);setLangOpen(false)}}
                      >
                        <span>{item.label}</span>
                        <span className="mielley-language-flag">{item.flag}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <button type="button" className="mielley-a11y-close" onClick={()=>setOpen(false)} aria-label={t.close}>×</button>
            </div>

            <header className="mielley-a11y-hero">
              <div className="mielley-a11y-hero-icon"><AccessibilityIcon/></div>
              <h2 id="mielley-a11y-title">{t.title}</h2>
              <p>{t.subtitle}</p>
            </header>

            <div className="mielley-a11y-content">
              <section className="mielley-a11y-section">
                <h3>{t.display}</h3>
                <div className="mielley-a11y-grid">
                  <button type="button" className={`mielley-a11y-card ${state.readableFont?'is-active':''}`} onClick={()=>toggle('readableFont')} aria-pressed={state.readableFont}>
                    <span className="mielley-a11y-card-icon">A</span><strong>{t.readable}</strong>
                  </button>

                  <article className="mielley-a11y-card mielley-a11y-card-wide">
                    <div><strong>{t.fontSize}</strong><small>{Math.round(state.fontScale*100)}%</small></div>
                    <div className="mielley-a11y-stepper">
                      <button type="button" onClick={()=>adjust('fontScale',-.1,.9,1.4)}>−</button>
                      <span>{state.fontScale===1?t.default:`${Math.round(state.fontScale*100)}%`}</span>
                      <button type="button" onClick={()=>adjust('fontScale',.1,.9,1.4)}>+</button>
                    </div>
                  </article>

                  <button type="button" className={`mielley-a11y-card ${state.largeCursor?'is-active':''}`} onClick={()=>toggle('largeCursor')} aria-pressed={state.largeCursor}>
                    <span className="mielley-a11y-card-icon">↖</span><strong>{t.bigCursor}</strong>
                  </button>

                  <article className="mielley-a11y-card mielley-a11y-card-wide">
                    <div><strong>{t.lineHeight}</strong><small>{state.lineHeight===1?t.default:`${state.lineHeight.toFixed(1)}×`}</small></div>
                    <div className="mielley-a11y-stepper">
                      <button type="button" onClick={()=>adjust('lineHeight',-.2,1,2)}>−</button>
                      <span>{state.lineHeight===1?t.default:`${state.lineHeight.toFixed(1)}×`}</span>
                      <button type="button" onClick={()=>adjust('lineHeight',.2,1,2)}>+</button>
                    </div>
                  </article>

                  <article className="mielley-a11y-card">
                    <span className="mielley-a11y-card-icon">B</span><strong>{t.fontWeight}</strong>
                    <div className="mielley-a11y-mini-stepper">
                      <button type="button" onClick={()=>adjust('fontWeight',-100,300,800)}>−</button>
                      <span>{state.fontWeight}</span>
                      <button type="button" onClick={()=>adjust('fontWeight',100,300,800)}>+</button>
                    </div>
                  </article>

                  <button type="button" className={`mielley-a11y-card ${state.alignText!=='default'?'is-active':''}`} onClick={cycleAlign}>
                    <span className="mielley-a11y-card-icon">☰</span><strong>{t.align}</strong><small>{alignLabel()}</small>
                  </button>

                  <article className="mielley-a11y-card">
                    <span className="mielley-a11y-card-icon">↔</span><strong>{t.letterSpacing}</strong>
                    <div className="mielley-a11y-mini-stepper">
                      <button type="button" onClick={()=>adjust('letterSpacing',-.02,0,.12)}>−</button>
                      <span>{state.letterSpacing===0?t.default:`${state.letterSpacing.toFixed(2)}em`}</span>
                      <button type="button" onClick={()=>adjust('letterSpacing',.02,0,.12)}>+</button>
                    </div>
                  </article>
                </div>
              </section>

              <section className="mielley-a11y-section">
                <h3>{t.colors}</h3>
                <div className="mielley-a11y-grid mielley-a11y-grid-3">
                  <button type="button" className={`mielley-a11y-card ${state.monochrome?'is-active':''}`} onClick={()=>toggle('monochrome')} aria-pressed={state.monochrome}>
                    <span className="mielley-a11y-card-icon">◑</span><strong>{t.monochrome}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.highContrast?'is-active':''}`} onClick={()=>toggle('highContrast')} aria-pressed={state.highContrast}>
                    <span className="mielley-a11y-card-icon">◐</span><strong>{t.highContrast}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.lightContrast?'is-active':''}`} onClick={()=>toggle('lightContrast')} aria-pressed={state.lightContrast}>
                    <span className="mielley-a11y-card-icon">☼</span><strong>{t.lightContrast}</strong>
                  </button>
                </div>
              </section>

              <section className="mielley-a11y-section">
                <h3>{t.orientation}</h3>
                <div className="mielley-a11y-grid mielley-a11y-grid-3">
                  <button type="button" className={`mielley-a11y-card ${state.hideImages?'is-active':''}`} onClick={()=>toggle('hideImages')} aria-pressed={state.hideImages}>
                    <span className="mielley-a11y-card-icon">▧</span><strong>{t.hideImages}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.readingMask?'is-active':''}`} onClick={()=>toggle('readingMask')} aria-pressed={state.readingMask}>
                    <span className="mielley-a11y-card-icon">▰</span><strong>{t.readingMask}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.readingLine?'is-active':''}`} onClick={()=>toggle('readingLine')} aria-pressed={state.readingLine}>
                    <span className="mielley-a11y-card-icon">━</span><strong>{t.readingLine}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.underlineLinks?'is-active':''}`} onClick={()=>toggle('underlineLinks')} aria-pressed={state.underlineLinks}>
                    <span className="mielley-a11y-card-icon">🔗</span><strong>{t.links}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.pauseMotion?'is-active':''}`} onClick={()=>toggle('pauseMotion')} aria-pressed={state.pauseMotion}>
                    <span className="mielley-a11y-card-icon">Ⅱ</span><strong>{t.stop}</strong>
                  </button>
                  <button type="button" className={`mielley-a11y-card ${state.highlightContent?'is-active':''}`} onClick={()=>toggle('highlightContent')} aria-pressed={state.highlightContent}>
                    <span className="mielley-a11y-card-icon">□</span><strong>{t.highlight}</strong>
                  </button>
                </div>
              </section>

              <section className="mielley-a11y-section mielley-a11y-skip">
                <h3>{t.skip}</h3>
                <select defaultValue="" onChange={e=>{jumpTo(e.target.value);e.currentTarget.value=''}}>
                  <option value="">{t.choose}</option>
                  <option value="main">{t.main}</option>
                  <option value="nav">{t.nav}</option>
                  <option value="footer">{t.footer}</option>
                </select>
              </section>

              <div className="mielley-a11y-actions">
                <button type="button" onClick={reset}>{t.reset}</button>
                <button type="button" onClick={()=>setOpen(false)}>{t.done}</button>
              </div>
              <p className="mielley-a11y-note">{t.note}</p>
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

export default AccessibilityWidget;
